/**
 * PSH Voxel Cube - 3D visualization of Phase Separated Hydrocarbons
 *
 * Creates a true 3D voxel representation of LNAPL/PSH floating on the water table.
 * Uses actual well gauging data (product_thickness_ft) with IDW interpolation.
 *
 * Key features (matches PSHCanvasLayer 2D logic):
 * - Convex hull boundary from wells with PSH
 * - Catmull-Rom boundary smoothing for organic curves
 * - IDW interpolation of thickness
 * - Edge feathering for smooth boundaries
 * - PSH floats ON TOP of water table (LNAPL physics)
 */

import React, { useMemo, useEffect } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { apiClient } from '../../api/client';

// =============================================================================
// CONSTANTS
// =============================================================================

// Scale: 1 unit = 10 feet (matches ThreePlume3DView)
const SCALE = 0.1;

// =============================================================================
// TYPES
// =============================================================================

export interface PSHWellData {
  well_id: string;
  lat: number;
  lon: number;
  product_thickness_ft: number;
  depth_to_water_ft?: number;
  depth_to_product_ft?: number;
}

interface Voxel3D {
  x: number;
  y: number;
  z: number;
  r: number;
  g: number;
  b: number;
  opacity: number;
}

interface PSHSurfaceMeshData {
  positions: Float32Array;
  indices: Uint32Array;
  colors: Float32Array;
  vertexCount: number;
}

// PSH colors - amber/orange gradient
const PSH_COLORS = {
  core: '#b45309',    // Dark amber (high thickness)
  mid: '#f59e0b',     // Amber (medium)
  outer: '#fcd34d',   // Light yellow (low thickness)
};

// Pre-parsed RGB values (0-1 range) — avoids THREE.Color allocations in hot loop
const PSH_RGB = {
  core:  { r: 0xb4 / 255, g: 0x53 / 255, b: 0x09 / 255 },  // #b45309
  mid:   { r: 0xf5 / 255, g: 0x9e / 255, b: 0x0b / 255 },  // #f59e0b
  outer: { r: 0xfc / 255, g: 0xd3 / 255, b: 0x4d / 255 },   // #fcd34d
  gray:  { r: 0.7, g: 0.6, b: 0.5 },
};

// Feet per degree for coordinate conversion
const FT_PER_DEG_LAT = 364000;

// =============================================================================
// SHADER MATERIAL (same as dissolved plume voxels)
// =============================================================================

function createPSHVoxelMaterial(emissiveIntensity: number = 0.4) {
  return new THREE.ShaderMaterial({
    uniforms: {
      emissiveColor: { value: new THREE.Color(PSH_COLORS.core) },
      emissiveIntensity: { value: emissiveIntensity },
    },
    vertexShader: `
      attribute vec3 instanceColor;
      attribute float instanceOpacity;
      varying vec3 vColor;
      varying float vOpacity;
      void main() {
        vColor = instanceColor;
        vOpacity = instanceOpacity;
        vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 emissiveColor;
      uniform float emissiveIntensity;
      varying vec3 vColor;
      varying float vOpacity;
      void main() {
        vec3 finalColor = vColor + emissiveColor * emissiveIntensity;
        gl_FragColor = vec4(finalColor, vOpacity);
      }
    `,
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
  });
}

// =============================================================================
// HELPER FUNCTIONS (same as PSHCanvasLayer)
// =============================================================================

function ftPerDegLon(lat: number): number {
  return FT_PER_DEG_LAT * Math.cos(lat * Math.PI / 180);
}

/**
 * Smooth boundary points using Catmull-Rom spline interpolation.
 * Creates organic, flowing curves instead of sharp angular boundaries.
 */
function smoothBoundaryPoints(
  points: [number, number][],
  iterations: number = 2,
  tension: number = 0.5
): [number, number][] {
  if (points.length < 3) return points;

  let smoothed = [...points];

  for (let iter = 0; iter < iterations; iter++) {
    const newPoints: [number, number][] = [];
    const n = smoothed.length;

    for (let i = 0; i < n; i++) {
      const p0 = smoothed[(i - 1 + n) % n];
      const p1 = smoothed[i];
      const p2 = smoothed[(i + 1) % n];
      const p3 = smoothed[(i + 2) % n];

      newPoints.push(p1);

      const t = 0.5;
      const t2 = t * t;
      const t3 = t2 * t;

      const s = (1 - tension) / 2;
      const b0 = -s * t3 + 2 * s * t2 - s * t;
      const b1 = (2 - s) * t3 + (s - 3) * t2 + 1;
      const b2 = (s - 2) * t3 + (3 - 2 * s) * t2 + s * t;
      const b3 = s * t3 - s * t2;

      newPoints.push([
        b0 * p0[0] + b1 * p1[0] + b2 * p2[0] + b3 * p3[0],
        b0 * p0[1] + b1 * p1[1] + b2 * p2[1] + b3 * p3[1],
      ]);
    }

    smoothed = newPoints;
  }

  return smoothed;
}

/**
 * Generate a circular boundary around a single point.
 */
function generateCircleBoundary3D(
  centerLat: number,
  centerLon: number,
  radiusFt: number,
  numPoints: number = 36
): [number, number][] {
  const lonFactor = ftPerDegLon(centerLat);
  const boundary: [number, number][] = [];
  for (let i = 0; i < numPoints; i++) {
    const angle = (2 * Math.PI * i) / numPoints;
    const dLat = (radiusFt * Math.sin(angle)) / FT_PER_DEG_LAT;
    const dLon = (radiusFt * Math.cos(angle)) / lonFactor;
    boundary.push([centerLat + dLat, centerLon + dLon]);
  }
  return boundary;
}

/**
 * Generate a capsule boundary between two wells.
 */
function generateCapsuleBoundary3D(
  well1: PSHWellData,
  well2: PSHWellData,
  radiusFt: number,
  numPoints: number = 36
): [number, number][] {
  const centerLat = (well1.lat + well2.lat) / 2;
  const lonFactor = ftPerDegLon(centerLat);
  const dx = (well2.lon - well1.lon) * lonFactor;
  const dy = (well2.lat - well1.lat) * FT_PER_DEG_LAT;
  const angle = Math.atan2(dy, dx);
  const boundary: [number, number][] = [];
  const halfPoints = Math.floor(numPoints / 2);
  for (let i = 0; i <= halfPoints; i++) {
    const a = angle - Math.PI / 2 + (Math.PI * i) / halfPoints;
    const dLat = (radiusFt * Math.sin(a)) / FT_PER_DEG_LAT;
    const dLon = (radiusFt * Math.cos(a)) / lonFactor;
    boundary.push([well2.lat + dLat, well2.lon + dLon]);
  }
  for (let i = 0; i <= halfPoints; i++) {
    const a = angle + Math.PI / 2 + (Math.PI * i) / halfPoints;
    const dLat = (radiusFt * Math.sin(a)) / FT_PER_DEG_LAT;
    const dLon = (radiusFt * Math.cos(a)) / lonFactor;
    boundary.push([well1.lat + dLat, well1.lon + dLon]);
  }
  return boundary;
}

/**
 * Generate convex hull from wells using Graham scan
 */
function convexHull(wells: PSHWellData[]): [number, number][] {
  if (wells.length < 3) {
    return wells.map(w => [w.lat, w.lon] as [number, number]);
  }

  const points = wells.map(w => ({ lat: w.lat, lon: w.lon }));

  // Find lowest point (min lat, then min lon)
  let lowest = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i].lat < points[lowest].lat ||
        (points[i].lat === points[lowest].lat && points[i].lon < points[lowest].lon)) {
      lowest = i;
    }
  }
  [points[0], points[lowest]] = [points[lowest], points[0]];

  const pivot = points[0];

  // Sort remaining points by polar angle around pivot
  // CRITICAL: slice() returns a new array — must capture the sorted result
  const sorted = points.slice(1).sort((a, b) => {
    const angleA = Math.atan2(a.lat - pivot.lat, a.lon - pivot.lon);
    const angleB = Math.atan2(b.lat - pivot.lat, b.lon - pivot.lon);
    return angleA - angleB;
  });

  // Graham scan on properly sorted points
  const cross = (o: typeof pivot, a: typeof pivot, b: typeof pivot) =>
    (a.lon - o.lon) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lon - o.lon);

  const hull: typeof points = [pivot, sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    while (hull.length > 1 && cross(hull[hull.length - 2], hull[hull.length - 1], sorted[i]) <= 0) {
      hull.pop();
    }
    hull.push(sorted[i]);
  }

  return hull.map(p => [p.lat, p.lon] as [number, number]);
}

/**
 * Expand boundary outward by a fixed distance (buffer)
 */
function expandBoundary(boundary: [number, number][], bufferFt: number, centerLat: number): [number, number][] {
  if (boundary.length < 3) return boundary;

  const centerLatB = boundary.reduce((sum, p) => sum + p[0], 0) / boundary.length;
  const centerLon = boundary.reduce((sum, p) => sum + p[1], 0) / boundary.length;

  const lonFactor = ftPerDegLon(centerLatB);

  return boundary.map(([lat, lon]) => {
    const dLat = lat - centerLatB;
    const dLon = lon - centerLon;
    const distFt = Math.sqrt((dLat * FT_PER_DEG_LAT) ** 2 + (dLon * lonFactor) ** 2);

    if (distFt < 1) return [lat, lon] as [number, number];

    const scale = (distFt + bufferFt) / distFt;
    return [
      centerLatB + dLat * scale,
      centerLon + dLon * scale,
    ] as [number, number];
  });
}

/**
 * Check if a point is inside a polygon using ray casting
 */
function isInsidePolygon(lat: number, lon: number, boundary: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
    const [yi, xi] = boundary[i];
    const [yj, xj] = boundary[j];
    if (((yi > lat) !== (yj > lat)) && (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Calculate distance from a point to the polygon boundary (in feet)
 */
function distanceToPolygonEdge(
  lat: number,
  lon: number,
  boundary: [number, number][],
  centerLat: number
): number {
  const lonFactor = ftPerDegLon(centerLat);
  let minDist = Infinity;

  for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
    const [y1, x1] = boundary[i];
    const [y2, x2] = boundary[j];

    const px = lon * lonFactor;
    const py = lat * FT_PER_DEG_LAT;
    const ax = x1 * lonFactor;
    const ay = y1 * FT_PER_DEG_LAT;
    const bx = x2 * lonFactor;
    const by = y2 * FT_PER_DEG_LAT;

    const abx = bx - ax;
    const aby = by - ay;
    const abLen2 = abx * abx + aby * aby;

    if (abLen2 < 0.001) {
      const dx = px - ax;
      const dy = py - ay;
      minDist = Math.min(minDist, Math.sqrt(dx * dx + dy * dy));
      continue;
    }

    const t = Math.max(0, Math.min(1, ((px - ax) * abx + (py - ay) * aby) / abLen2));
    const closestX = ax + t * abx;
    const closestY = ay + t * aby;

    const dx = px - closestX;
    const dy = py - closestY;
    minDist = Math.min(minDist, Math.sqrt(dx * dx + dy * dy));
  }

  return minDist;
}

/**
 * IDW interpolation of PSH thickness from well data.
 * Power=3 creates steeper gradients around wells for bell-curve-like profiles.
 */
function interpolatePSHThickness(
  lat: number,
  lon: number,
  wells: PSHWellData[],
  centerLat: number
): number {
  if (wells.length === 0) return 0;

  const lonFactor = ftPerDegLon(centerLat);
  let sumWeights = 0;
  let sumWeightedThickness = 0;
  const power = 3;

  for (const well of wells) {
    const dLat = (lat - well.lat) * FT_PER_DEG_LAT;
    const dLon = (lon - well.lon) * lonFactor;
    const dist = Math.sqrt(dLat * dLat + dLon * dLon);

    if (dist < 1) return well.product_thickness_ft;

    const weight = 1 / Math.pow(dist, power);
    sumWeights += weight;
    sumWeightedThickness += weight * well.product_thickness_ft;
  }

  return sumWeights > 0 ? sumWeightedThickness / sumWeights : 0;
}

/**
 * IDW interpolation of depth-to-water at a grid point from well gauging data.
 * Wells without depth_to_water_ft are skipped; falls back to the site-level default.
 */
function interpolateDepthToWater(
  lat: number,
  lon: number,
  wells: PSHWellData[],
  centerLat: number,
  fallbackDTW: number,
): number {
  const wellsWithDTW = wells.filter(w => w.depth_to_water_ft != null && w.depth_to_water_ft > 0);
  if (wellsWithDTW.length === 0) return fallbackDTW;

  const lonFactor = ftPerDegLon(centerLat);
  let sumWeights = 0;
  let sumWeightedDTW = 0;
  const power = 2; // Gentler falloff than thickness — water table is smoother

  for (const well of wellsWithDTW) {
    const dLat = (lat - well.lat) * FT_PER_DEG_LAT;
    const dLon = (lon - well.lon) * lonFactor;
    const dist = Math.sqrt(dLat * dLat + dLon * dLon);

    if (dist < 1) return well.depth_to_water_ft!;

    const weight = 1 / Math.pow(dist, power);
    sumWeights += weight;
    sumWeightedDTW += weight * well.depth_to_water_ft!;
  }

  return sumWeights > 0 ? sumWeightedDTW / sumWeights : fallbackDTW;
}

// =============================================================================
// VOXEL GRID GENERATION
// =============================================================================

interface PSHVoxelGridResult {
  voxels: Voxel3D[];
  voxelSizeXZ: number;
  voxelSizeY: number;
  verticalExaggeration: number;
  maxThicknessFt: number;
  topSurface: PSHSurfaceMeshData | null;
}

function generatePSHVoxelGrid(
  wells: PSHWellData[],
  sourceLatLon: { lat: number; lon: number },
  depthToWaterFt: number,
  gridResolutionXZ: number = 240,  // Horizontal resolution
  gridResolutionY: number = 10,   // Vertical resolution (layers within PSH)
  edgeFeatherFt: number = 30,     // Edge feathering distance
  verticalExaggeration: number = 1,  // 1× = true scale, up to 35× magnifier
): PSHVoxelGridResult {
  if (wells.length === 0) return { voxels: [], voxelSizeXZ: 0, voxelSizeY: 0, verticalExaggeration: 1, maxThicknessFt: 0, topSurface: null };

  // Generate boundary based on well count
  const sourceLat = sourceLatLon.lat;
  let boundary: [number, number][];

  if (wells.length === 1) {
    boundary = generateCircleBoundary3D(wells[0].lat, wells[0].lon, 80);
  } else if (wells.length === 2) {
    boundary = generateCapsuleBoundary3D(wells[0], wells[1], 80);
  } else {
    const rawBoundary = convexHull(wells);
    const expandedBoundary = expandBoundary(rawBoundary, 80, sourceLat);
    boundary = smoothBoundaryPoints(expandedBoundary, 3, 0.5);
  }

  const voxels: Voxel3D[] = [];
  const sourceLon = sourceLatLon.lon;
  const lonFactor = ftPerDegLon(sourceLat);

  // Calculate bounds from smoothed boundary
  const lats = boundary.map(p => p[0]);
  const lons = boundary.map(p => p[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);

  const centerLat = (minLat + maxLat) / 2;
  const latStep = (maxLat - minLat) / gridResolutionXZ;
  const lonStep = (maxLon - minLon) / gridResolutionXZ;

  // Skip degenerate bounds
  if (latStep <= 0 || lonStep <= 0) return { voxels: [], voxelSizeXZ: 0, voxelSizeY: 0, verticalExaggeration: 1, maxThicknessFt: 0, topSurface: null };

  // Max thickness for normalization
  const maxThickness = Math.max(...wells.map(w => w.product_thickness_ft));
  if (maxThickness <= 0) return { voxels: [], voxelSizeXZ: 0, voxelSizeY: 0, verticalExaggeration: 1, maxThicknessFt: 0, topSurface: null };

  // verticalExaggeration is passed in from the magnifier slider (1× – 35×)

  // Voxel sizes
  const voxelSizeXZft = Math.max(latStep * FT_PER_DEG_LAT, lonStep * lonFactor) * 1.2;
  const voxelSizeXZ = voxelSizeXZft * SCALE;

  // Vertical voxel size based on max thickness, with vertical exaggeration applied
  const voxelSizeYft = maxThickness / gridResolutionY;
  const voxelSizeY = voxelSizeYft * SCALE * verticalExaggeration;

  // Generate voxels within the smooth boundary
  // Water table depth varies spatially based on per-well gauging data (IDW interpolation)
  for (let i = 0; i <= gridResolutionXZ; i++) {
    for (let j = 0; j <= gridResolutionXZ; j++) {
      const lat = minLat + i * latStep;
      const lon = minLon + j * lonStep;

      // Only render inside the smoothed polygon boundary
      if (!isInsidePolygon(lat, lon, boundary)) continue;

      // Interpolate PSH thickness at this location
      const rawThickness = interpolatePSHThickness(lat, lon, wells, centerLat);
      // Bell-curve sharpening: pow(t/max, 1.5) makes peak sharper, sides steeper
      const thickness = maxThickness * Math.pow(Math.min(1, rawThickness / maxThickness), 1.5);
      const thicknessRatio = Math.min(1, Math.max(0, thickness / maxThickness));

      if (thicknessRatio < 0.02) continue;  // Skip very low thickness cells

      // Edge feathering - fade out near polygon boundary
      const edgeDist = distanceToPolygonEdge(lat, lon, boundary, centerLat);
      const edgeFactor = Math.min(1, edgeDist / edgeFeatherFt);
      // Smoothstep for natural falloff
      const smoothEdgeFactor = edgeFactor * edgeFactor * (3 - 2 * edgeFactor);

      // Calculate number of vertical voxels for this column
      const numVerticalVoxels = Math.max(1, Math.ceil(thickness / voxelSizeYft));

      // Convert to 3D coordinates
      const eastFt = (lon - sourceLon) * lonFactor;
      const northFt = (lat - sourceLat) * FT_PER_DEG_LAT;
      const x = eastFt * SCALE;
      const z = -northFt * SCALE;

      // Per-cell water table depth from gauging data (spatially varying)
      const localDTW = interpolateDepthToWater(lat, lon, wells, centerLat, depthToWaterFt);
      const localWaterTableY = -localDTW * SCALE;

      // Generate vertical stack of voxels
      for (let k = 0; k < numVerticalVoxels; k++) {
        // Y position: stack from local water table upward
        const y = localWaterTableY + (k + 0.5) * voxelSizeY;

        // Depth within PSH (0 = top, 1 = bottom)
        const depthRatio = k / numVerticalVoxels;

        // Color: darker at bottom (denser), lighter at top
        const { r, g, b } = getPSHColor(depthRatio, thickness, maxThickness);

        // Opacity with edge feathering
        const baseOpacity = 0.4 + thicknessRatio * 0.55;
        const opacity = baseOpacity * smoothEdgeFactor;

        voxels.push({ x, y, z, r, g, b, opacity });
      }
    }
  }

  // =========================================================================
  // Generate smooth top-surface mesh for 3D lens/dome shape
  // Lower resolution than voxel grid since it's just a surface
  // =========================================================================
  const surfaceRes = 80;
  const surfLatStep = (maxLat - minLat) / surfaceRes;
  const surfLonStep = (maxLon - minLon) / surfaceRes;

  const surfPositions: number[] = [];
  const surfColors: number[] = [];
  const surfVertexMap: (number | -1)[][] = [];
  let surfVIdx = 0;

  for (let i = 0; i <= surfaceRes; i++) {
    surfVertexMap[i] = [];
    for (let j = 0; j <= surfaceRes; j++) {
      const lat = minLat + i * surfLatStep;
      const lon = minLon + j * surfLonStep;

      if (!isInsidePolygon(lat, lon, boundary)) {
        surfVertexMap[i][j] = -1;
        continue;
      }

      const rawThickness = interpolatePSHThickness(lat, lon, wells, centerLat);
      // Bell-curve sharpening — same transform as voxel loop
      const thickness = maxThickness * Math.pow(Math.min(1, rawThickness / maxThickness), 1.5);
      const edgeDist = distanceToPolygonEdge(lat, lon, boundary, centerLat);
      const edgeFactor = Math.min(1, edgeDist / edgeFeatherFt);
      const smoothEdge = edgeFactor * edgeFactor * (3 - 2 * edgeFactor);
      const effectiveThickness = thickness * smoothEdge;

      const eastFt = (lon - sourceLon) * lonFactor;
      const northFt = (lat - sourceLat) * FT_PER_DEG_LAT;
      const x = eastFt * SCALE;
      const z = -northFt * SCALE;
      // Per-cell water table depth from gauging data (spatially varying)
      const localDTW = interpolateDepthToWater(lat, lon, wells, centerLat, depthToWaterFt);
      const localWaterTableY = -localDTW * SCALE;
      const y = localWaterTableY + effectiveThickness * SCALE * verticalExaggeration;

      surfPositions.push(x, y, z);

      // 3-tier gradient based on thickness ratio: outer → mid → core
      const ratio = Math.min(1, effectiveThickness / maxThickness);
      const coreCol = new THREE.Color(PSH_COLORS.core);   // #b45309 dark amber
      const midCol = new THREE.Color(PSH_COLORS.mid);     // #f59e0b amber
      const outerCol = new THREE.Color(PSH_COLORS.outer); // #fcd34d light yellow
      let col: THREE.Color;
      if (ratio > 0.6) {
        // High thickness — amber to dark amber
        col = midCol.clone().lerp(coreCol, (ratio - 0.6) / 0.4);
      } else if (ratio > 0.25) {
        // Medium thickness — light yellow to amber
        col = outerCol.clone().lerp(midCol, (ratio - 0.25) / 0.35);
      } else {
        // Low thickness — pale/desaturated to light yellow
        const paleCol = new THREE.Color(0.85, 0.78, 0.6);
        col = paleCol.clone().lerp(outerCol, ratio / 0.25);
      }
      // Extra desaturation for very thin PSH
      if (ratio < 0.15) {
        const gray = new THREE.Color(0.7, 0.65, 0.55);
        col.lerp(gray, (0.15 - ratio) / 0.15 * 0.5);
      }
      surfColors.push(col.r, col.g, col.b);

      surfVertexMap[i][j] = surfVIdx++;
    }
  }

  // Build triangle indices — only where all 3 vertices exist inside boundary
  const surfIndices: number[] = [];
  for (let i = 0; i < surfaceRes; i++) {
    for (let j = 0; j < surfaceRes; j++) {
      const a = surfVertexMap[i][j];
      const b = surfVertexMap[i][j + 1];
      const c = surfVertexMap[i + 1][j];
      const d = surfVertexMap[i + 1][j + 1];

      if (a !== -1 && b !== -1 && d !== -1) {
        surfIndices.push(a, b, d);
      }
      if (a !== -1 && d !== -1 && c !== -1) {
        surfIndices.push(a, d, c);
      }
    }
  }

  const topSurface: PSHSurfaceMeshData | null =
    surfPositions.length > 0 && surfIndices.length > 0
      ? {
          positions: new Float32Array(surfPositions),
          indices: new Uint32Array(surfIndices),
          colors: new Float32Array(surfColors),
          vertexCount: surfVIdx,
        }
      : null;

  return { voxels, voxelSizeXZ, voxelSizeY, verticalExaggeration, maxThicknessFt: maxThickness, topSurface };
}

/**
 * Get PSH color based on depth within the LNAPL layer and thickness.
 */
function getPSHColor(depthRatio: number, thickness: number, maxThickness: number): { r: number; g: number; b: number } {
  const thicknessRatio = thickness / maxThickness;

  // Linear interpolation helper (inline to avoid function call overhead)
  let r: number, g: number, b: number;

  if (depthRatio > 0.7) {
    // Bottom layer - dark amber (core)
    r = PSH_RGB.core.r; g = PSH_RGB.core.g; b = PSH_RGB.core.b;
  } else if (depthRatio > 0.3) {
    // Middle layer - lerp mid → core
    const t = (depthRatio - 0.3) / 0.4;
    r = PSH_RGB.mid.r + (PSH_RGB.core.r - PSH_RGB.mid.r) * t;
    g = PSH_RGB.mid.g + (PSH_RGB.core.g - PSH_RGB.mid.g) * t;
    b = PSH_RGB.mid.b + (PSH_RGB.core.b - PSH_RGB.mid.b) * t;
  } else {
    // Top layer - lerp outer → mid
    const t = depthRatio / 0.3;
    r = PSH_RGB.outer.r + (PSH_RGB.mid.r - PSH_RGB.outer.r) * t;
    g = PSH_RGB.outer.g + (PSH_RGB.mid.g - PSH_RGB.outer.g) * t;
    b = PSH_RGB.outer.b + (PSH_RGB.mid.b - PSH_RGB.outer.b) * t;
  }

  // Desaturate slightly where PSH is thin
  if (thicknessRatio < 0.3) {
    const f = (0.3 - thicknessRatio) * 0.5;
    r += (PSH_RGB.gray.r - r) * f;
    g += (PSH_RGB.gray.g - g) * f;
    b += (PSH_RGB.gray.b - b) * f;
  }

  return { r, g, b };
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export interface PSHMetadata {
  verticalExaggeration: number;
  maxThicknessFt: number;
}

export interface PSHVoxelCubeProps {
  wells: PSHWellData[];
  sourceLatLon: { lat: number; lon: number };
  depthToWaterFt: number;
  gridResolutionXZ?: number;
  gridResolutionY?: number;
  edgeFeatherFt?: number;
  visible?: boolean;
  onMetadata?: (meta: PSHMetadata) => void;
  verticalExaggeration?: number;  // 1 = true scale, up to 35× magnifier
}

export const PSHVoxelCube = React.memo(function PSHVoxelCube({
  wells,
  sourceLatLon,
  depthToWaterFt,
  gridResolutionXZ = 240,
  gridResolutionY = 10,
  edgeFeatherFt = 50,
  visible = true,
  onMetadata,
  verticalExaggeration = 1,
}: PSHVoxelCubeProps) {
  // Generate voxel grid + surface mesh with convex hull, boundary smoothing, and edge feathering
  const { voxels, voxelSizeXZ, voxelSizeY, maxThicknessFt, topSurface } = useMemo(() => {
    if (!visible || wells.length === 0) return { voxels: [], voxelSizeXZ: 0, voxelSizeY: 0, verticalExaggeration: 1, maxThicknessFt: 0, topSurface: null };
    return generatePSHVoxelGrid(
      wells,
      sourceLatLon,
      depthToWaterFt,
      gridResolutionXZ,
      gridResolutionY,
      edgeFeatherFt,
      verticalExaggeration,
    );
  }, [wells, sourceLatLon, depthToWaterFt, gridResolutionXZ, gridResolutionY, edgeFeatherFt, visible, verticalExaggeration]);

  // Fire metadata callback when VE/thickness changes
  useEffect(() => {
    if (onMetadata && maxThicknessFt > 0) {
      onMetadata({ verticalExaggeration, maxThicknessFt });
    }
  }, [verticalExaggeration, maxThicknessFt, onMetadata]);

  // Build THREE.BufferGeometry from surface mesh data
  const topSurfaceGeo = useMemo(() => {
    if (!topSurface) return null;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(topSurface.positions, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(topSurface.colors, 3));
    geo.setIndex(new THREE.Uint32BufferAttribute(topSurface.indices, 1));
    geo.computeVertexNormals();
    return geo;
  }, [topSurface]);

  // Solid material for the surface mesh — the primary 3D mountain shape
  const surfaceMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    vertexColors: true,
    transparent: false,
    side: THREE.DoubleSide,
    roughness: 0.5,
    metalness: 0.0,
  }), []);

  // Wireframe overlay to show mountain topography clearly
  const wireframeMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    color: '#92400e',
    wireframe: true,
    transparent: true,
    opacity: 0.3,
  }), []);

  // Label text for VE indicator
  const labelText = maxThicknessFt > 0
    ? `PSH (${maxThicknessFt.toFixed(2)} ft max, ${Math.round(verticalExaggeration)}x VE)`
    : '';

  // Position label above the tallest voxel column
  const waterTableY = -depthToWaterFt * SCALE;
  const labelY = waterTableY + maxThicknessFt * SCALE * verticalExaggeration + 0.4;

  if (!visible || !topSurfaceGeo) return null;

  return (
    <group>
      {/* 3D mountain surface mesh — the primary PSH visualization */}
      {topSurfaceGeo && (
        <>
          <mesh geometry={topSurfaceGeo} material={surfaceMaterial} frustumCulled={false} />
          <mesh geometry={topSurfaceGeo} material={wireframeMaterial} frustumCulled={false} />
        </>
      )}
      {labelText && (
        <Text
          position={[0, labelY, 0]}
          fontSize={0.35}
          color="#f59e0b"
          anchorX="center"
          anchorY="bottom"
          outlineWidth={0.02}
          outlineColor="#000000"
        >
          {labelText}
        </Text>
      )}
    </group>
  );
});

// =============================================================================
// HOOK FOR FETCHING PSH DATA
// =============================================================================

export interface UsePSHDataResult {
  wells: PSHWellData[];
  loading: boolean;
  error: string | null;
  maxThickness: number;
  avgThickness: number;
}

/**
 * Hook to fetch PSH well data from the API.
 * Usage: const { wells, loading, error } = usePSHData(siteId);
 *
 * @param siteId - Site ID to fetch PSH data for
 * @param targetDate - Optional date (YYYY-MM-DD) to fetch historical PSH data.
 *                     If provided, fetches data at or before this date.
 *                     If null/undefined, fetches latest data.
 */
// Module-level dedup cache: prevents duplicate in-flight requests for the same key
const _pshInflight = new Map<string, Promise<PSHWellData[]>>();

export function usePSHData(siteId: string | null, targetDate?: string | null): UsePSHDataResult {
  const [wells, setWells] = React.useState<PSHWellData[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!siteId) {
      setWells([]);
      return;
    }

    const cacheKey = `${siteId}:${targetDate ?? 'latest'}`;

    const fetchPSHData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Dedup across multiple hook instances (ThreePlumeMap + ThreePlume3DView)
        // and StrictMode double-mounts: share the same in-flight promise
        let promise = _pshInflight.get(cacheKey);
        if (!promise) {
          const endpoint = targetDate
            ? `/polygon/psh/site/${siteId}/at-date/${targetDate}`
            : `/polygon/psh/site/${siteId}/latest`;

          promise = apiClient.get<any[]>(endpoint, {
            params: { infer_interior: true },
          }).then(response => response.data.map((m: any) => ({
            well_id: m.well_id,
            lat: m.lat,
            lon: m.lon,
            product_thickness_ft: m.product_thickness_ft,
            depth_to_water_ft: m.depth_to_water_ft,
            depth_to_product_ft: m.depth_to_product_ft,
          })));
          _pshInflight.set(cacheKey, promise);
          promise.finally(() => _pshInflight.delete(cacheKey));
        }

        const mappedWells = await promise;
        setWells(mappedWells);
      } catch (err) {
        console.error('[usePSHData] Error:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setWells([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPSHData();
  }, [siteId, targetDate]);

  const maxThickness = wells.length > 0 ? Math.max(...wells.map(w => w.product_thickness_ft)) : 0;
  const avgThickness = wells.length > 0
    ? wells.reduce((sum, w) => sum + w.product_thickness_ft, 0) / wells.length
    : 0;

  return { wells, loading, error, maxThickness, avgThickness };
}

export default PSHVoxelCube;
